import { useCallback, useState } from 'react';

export type SortDir = 'asc' | 'desc';

/** Column sort over a getter map; values compare as numbers when both parse (same rule as apps/web). */
export function sortRows<T>(
  rows: T[],
  column: string | null,
  dir: SortDir,
  getters: Record<string, (row: T) => string | number | null | undefined>,
): T[] {
  if (!column) return rows;
  const get = getters[column];
  if (!get) return rows;
  const mul = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = get(a);
    const bv = get(b);
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    const an = typeof av === 'number' ? av : Number(av);
    const bn = typeof bv === 'number' ? bv : Number(bv);
    if (!Number.isNaN(an) && !Number.isNaN(bn) && String(av).trim() !== '' && String(bv).trim() !== '') {
      return (an - bn) * mul;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * mul;
  });
}

/** Local (non-URL) sort state for the `Th` header control. */
export function useTableSort(initialColumn: string | null = null, initialDir: SortDir = 'asc') {
  const [column, setColumn] = useState<string | null>(initialColumn);
  const [dir, setDir] = useState<SortDir>(initialDir);
  const toggle = useCallback(
    (next: string) => {
      if (next === column) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else {
        setColumn(next);
        setDir('asc');
      }
    },
    [column],
  );
  return { column, dir, toggle };
}
